/**
 * Undo and redo.
 *
 * **Scaffold.** The stack, the entry shape and the memory accounting are real
 * and tested; nothing pushes to it yet.
 *
 * The design only works because of the wet layer. A stroke used to be 122
 * separate fills onto the artwork (see docs/decisions/tools-and-the-wet-layer.md)
 * with no moment that could be called "the edit". Now it commits exactly once,
 * so an undo entry is a layer id, a rect, and that rect's pixels before and
 * after:
 *
 *     128x128 rect of RGBA          = 64 kB
 *     100 of those                  = 6 MB      ← fine
 *     100 full-canvas snapshots     = 130 MB    ← not fine
 *
 * Rect-scoped entries are the whole trick. Tile-based stores are the other
 * established answer and are what you need for *non-linear* history — undoing
 * one edit while keeping later ones. We do not want that, so we do not pay for
 * it.
 *
 * Structure edits are the exception that proves it: undoing a delete means
 * producing pixels that no longer exist anywhere else, so that entry does hold
 * a whole canvas, and the byte accounting charges it for exactly that.
 */
import type { RegionId } from "../../shared/format";
import type { FabricSource } from "./fabric-source";
import type { RuntimeLayer } from "./layers";
import type { DirtyRect } from "./paint";

/** Pixels replaced within one rect of one layer. The common case by far. */
export interface PixelEdit {
  kind: "pixels";
  label: string;
  layerId: string;
  rect: DirtyRect;
  before: ImageData;
  after: ImageData;
  /**
   * What the layer was made of, either side of the edit.
   *
   * A fabric record describes a specific set of pixels — it carries the `rev`
   * those pixels had — so an edit that puts different pixels back has to put
   * the matching record back with them. Without this, undoing a recolour left
   * the record pointing at a rev the layer no longer had: the colour dot
   * vanished for good, and the stale record claimed a tint the pixels did not
   * have.
   *
   * Optional because most pixel edits are brush strokes on layers that were
   * never a fabric, and `undefined` there means "leave it alone" — only an
   * edit that says something about the record gets to change it.
   */
  fabricBefore?: FabricSource | null;
  fabricAfter?: FabricSource | null;
}

/**
 * Anything that changed the stack's shape rather than its pixels — reorder,
 * add, delete, visibility, rename, duplicate, merge.
 *
 * Stored as the whole layer list either side, not as a diff: twenty layers is
 * a few hundred bytes of metadata, and reconstructing a diff would be more code
 * than storing both.
 *
 * **The list holds the layers themselves, canvas included.** That is not
 * incidental — it is the only way to undo a delete. A snapshot of a deleted
 * layer's name and opacity restores a row with nothing in it, which is worse
 * than not restoring it at all, because it looks like the work came back.
 *
 * The canvases are shared with the live stack, not copied. A layer that is
 * still in the stack costs nothing extra to reference; only a layer that was
 * removed is genuinely held alive by this, which is exactly the memory we mean
 * to spend.
 */
export interface StructureEdit {
  kind: "structure";
  label: string;
  before: LayerSnapshot[];
  after: LayerSnapshot[];
}

/** The layer list as it stood — the live objects, in the order they were in. */
export type LayerSnapshot = RuntimeLayer;

/**
 * Which faces painting is limited to.
 *
 * Not document state — a selection is a tool mode and reaches no file. It is in
 * this stack anyway because a selection is BUILT, sometimes carefully, and work
 * that is built is work that can be lost. `shared/selection.ts` says a selection
 * "records no history"; that is right about persistence and was wrong about
 * undo.
 *
 * The usual objection to non-visual entries is that ⌘Z appears to do nothing.
 * This stack already answers that for a different reason — an undo in a layered
 * document is often a change on a layer that is not on screen — so every undo
 * announces what it was. "Undid: select right arm" is not a mystery.
 *
 * One GESTURE is one entry. A drag across four faces is one step, not four, or
 * undoing a limb selection takes six presses and the feature is worse than not
 * having it.
 */
export interface SelectionEdit {
  kind: "selection";
  label: string;
  before: RegionId[];
  after: RegionId[];
}

export type Edit = PixelEdit | StructureEdit | SelectionEdit;

/**
 * How much of the past to keep.
 *
 * A cap in *bytes* rather than in steps, because the steps are wildly uneven —
 * a dab on a sleeve and a fill across the torso differ by three orders of
 * magnitude. Counting steps means either a stingy limit for small edits or an
 * unbounded one for large edits, and the second is how a paint app on a
 * Chromebook dies.
 */
export const MAX_HISTORY_BYTES = 64 * 1024 * 1024;

/**
 * What an entry is responsible for keeping alive.
 *
 * For a structure edit that is **only the layers it removed**. Every other
 * layer in the snapshot is still in the live stack, referenced rather than
 * retained, and costs nothing — counting those would charge a reorder of ten
 * layers 26 MB and evict the paint history behind it within two moves.
 *
 * Metadata is counted at a flat 64 bytes a layer. It is an estimate, and it is
 * three orders of magnitude below the thing that actually matters.
 */
function editBytes(edit: Edit): number {
  /*
    Eighteen short strings at the very most, so a flat estimate — the same
    treatment structure edits give their metadata, and for the same reason: it
    is three orders of magnitude below the thing the cap exists to bound.

    It needs a case at all because without one this falls through to the pixel
    branch and reads `.data.byteLength` off a field a selection edit does not
    have. That is `undefined`, which poisons the running total and quietly
    disables the byte cap for the rest of the session.
  */
  if (edit.kind === "selection") return (edit.before.length + edit.after.length) * 32;
  if (edit.kind === "structure") {
    const kept = new Set(edit.after.map((l) => l.id));
    let bytes = (edit.before.length + edit.after.length) * 64;
    for (const layer of edit.before) {
      if (kept.has(layer.id)) continue;
      bytes += layer.source.width * layer.source.height * 4;
    }
    return bytes;
  }
  return edit.before.data.byteLength + edit.after.data.byteLength;
}

export interface HistoryOptions {
  /** Applies an edit in the given direction. The stack never touches pixels. */
  apply: (edit: Edit, direction: "undo" | "redo") => void;
  onChange?: (state: { canUndo: boolean; canRedo: boolean; bytes: number }) => void;
}

export function createHistory(opts: HistoryOptions) {
  const past: Edit[] = [];
  const future: Edit[] = [];
  let bytes = 0;

  const state = () => ({
    canUndo: past.length > 0,
    canRedo: future.length > 0,
    bytes,
  });
  const emit = () => opts.onChange?.(state());

  /** Drop the oldest entries until we are back under the cap. */
  function trim() {
    while (bytes > MAX_HISTORY_BYTES && past.length > 1) {
      const dropped = past.shift();
      if (dropped) bytes -= editBytes(dropped);
    }
  }

  return {
    push(edit: Edit) {
      past.push(edit);
      bytes += editBytes(edit);
      // A new edit invalidates the redo branch. Keeping it would mean a tree,
      // and a tree needs a UI nobody asked for.
      if (future.length) {
        for (const e of future) bytes -= editBytes(e);
        future.length = 0;
      }
      trim();
      emit();
    },

    undo(): Edit | null {
      const edit = past.pop();
      if (!edit) return null;
      opts.apply(edit, "undo");
      future.push(edit);
      emit();
      return edit;
    },

    redo(): Edit | null {
      const edit = future.pop();
      if (!edit) return null;
      opts.apply(edit, "redo");
      past.push(edit);
      emit();
      return edit;
    },

    /** For "Undid: brush stroke" — an undo in a layered document is otherwise a
     *  small silent change somewhere off screen. */
    peekUndoLabel(): string | null {
      return past.at(-1)?.label ?? null;
    },
    peekRedoLabel(): string | null {
      return future.at(-1)?.label ?? null;
    },

    clear() {
      past.length = 0;
      future.length = 0;
      bytes = 0;
      emit();
    },

    get state() {
      return state();
    },
  };
}

export type History = ReturnType<typeof createHistory>;
